interface GetCyclicDistanceParams {
  firstIndex: number;
  secondIndex: number;
  mod: number;
}

export function getCyclicDistance({
  firstIndex,
  secondIndex,
  mod,
}: GetCyclicDistanceParams): number {
  const raw = Math.abs(firstIndex - secondIndex);
  return Math.min(raw, mod - raw);
}

export interface GetLoopResetLandingIndexParams {
  index: number;
  originalLength: number;
  middleCopyStart: number;
  middleCopyEnd: number;
}

enum LoopResetRegion {
  BeforeMiddle = "beforeMiddle",
  Middle = "middle",
  AfterMiddle = "afterMiddle",
}

interface LoopResetRule {
  isMatch: (params: GetLoopResetLandingIndexParams) => boolean;
  getLandingIndex: (params: GetLoopResetLandingIndexParams) => number;
}

const LOOP_RESET_RULES: Record<LoopResetRegion, LoopResetRule> = {
  [LoopResetRegion.BeforeMiddle]: {
    isMatch: ({ index, middleCopyStart }) => index < middleCopyStart,
    getLandingIndex: ({ index, originalLength }) => index + originalLength,
  },
  [LoopResetRegion.AfterMiddle]: {
    isMatch: ({ index, middleCopyEnd }) => index >= middleCopyEnd,
    getLandingIndex: ({ index, originalLength }) => index - originalLength,
  },
  [LoopResetRegion.Middle]: {
    isMatch: () => true,
    getLandingIndex: ({ index }) => index,
  },
};

const LOOP_RESET_REGION_ORDER: LoopResetRegion[] = [
  LoopResetRegion.BeforeMiddle,
  LoopResetRegion.AfterMiddle,
  LoopResetRegion.Middle,
];

export function getLoopResetLandingIndex(
  params: GetLoopResetLandingIndexParams,
): number {
  const region =
    LOOP_RESET_REGION_ORDER.find((candidate) =>
      LOOP_RESET_RULES[candidate].isMatch(params),
    ) ?? LoopResetRegion.Middle;

  return LOOP_RESET_RULES[region].getLandingIndex(params);
}

interface GetOriginalTeamIndexParams {
  doubledIndex: number;
  originalLength: number;
}

export function getOriginalTeamIndex({
  doubledIndex,
  originalLength,
}: GetOriginalTeamIndexParams): number {
  return ((doubledIndex % originalLength) + originalLength) % originalLength;
}
